/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Lineage List
 * Feature #197: Portal Light-Mode Theme, Modals & Responsive UX
 * Constitution: v1.20.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GitBranch } from 'lucide-react';
import { useLineageList } from '../../services/operations/metadataService';
import type { LineageListItem } from '../../services/operations/metadataService';
import { LineageGraphPage } from './LineageGraphPage';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  Button,
  Modal,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

const columns: DataTableColumn<LineageListItem>[] = [
  { key: 'itemName', header: 'Item', width: '30%' },
  {
    key: 'itemType',
    header: 'Type',
    width: '20%',
    render: (r) => <Badge variant="default">{r.itemType}</Badge>,
  },
  { key: 'workspaceName', header: 'Workspace', width: '30%' },
  { key: 'sourceCount', header: 'Sources', width: '20%', align: 'right' },
];

export const LineageListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useLineageList();
  // Lineage detail opens in a kit Modal (embedded graph) instead of a full-page nav.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <PageLayout
      helpId="auto.lineage-list"
      title="Item Lineage"
      description="Fabric items with upstream lineage data collected"
      icon={<GitBranch className="h-6 w-6" />}
      actions={
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/metadata')}>
          Metadata
        </Button>
      }
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState
          title="Failed to load lineage data"
          message="Unable to retrieve lineage list. Please try again."
        />
      ) : !data?.length ? (
        <EmptyState
          title="No lineage data collected"
          message="No Fabric items with lineage information have been collected yet. Run the daily collection job to populate this list."
        />
      ) : (
        <Card padding="none">
          <DataTable
            columns={columns}
            rows={data}
            rowKey={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            emptyMessage="No items with lineage data collected yet."
          />
        </Card>
      )}

      {/* Lineage graph in the kit Modal (embedded, no full-page nav). */}
      <Modal
        isOpen={!!selectedId}
        onClose={() => setSelectedId(null)}
        title="Item Lineage"
        size="xl"
        className="p-0"
      >
        {selectedId && <LineageGraphPage embeddedId={selectedId} />}
      </Modal>
    </PageLayout>
  );
};
